import { handleHotspotClick } from "./layers/hotspots/HotspotsLayer.class"
import { _CameraLight, AmbientLight, LayersList, LightingEffect, PickingInfo } from "@deck.gl/core"
// deckgl dependency, I don't want to control the version
// oxlint-disable-next-line nova/no-implicit-dependencies
import { MjolnirGestureEvent } from "mjolnir.js"
import { LayerId } from "./layers/constants"
import React, { useCallback, useMemo, useState } from "react"
import { selectLayerDropdownOpen } from "./control/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { DeckGLOverlay } from "./MapPreview"
import { MapboxCursors } from "./layers/cursors/MapboxCursors"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectActiveToolName } from "plugins/Toolbox/tool-registry"
import { isMapToolActive } from "./layers/pickingGuard"
import { MapboxLabel } from "./layers/hotspots/MapboxLabel"
import { selectHasVisibleLabels } from "features/entity-caching/flights/selectors"
import { ContextMenuPosition } from "./types"
import { MapbboxCursorLiveChatInput } from "./layers/cursors/MapboxCursorLiveChatInput"
import { SearchMarker } from "./search/MapSearchMarker"
import MapViewStateController from "./MapViewStateController"
import type { TooltipContent } from "features/map/types"
import EventEmitter from "events"
import { selectEditingFirePerimeterId } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { MapContextMenuContainer } from "./context-menu/MapContextMenuContainer"
import { BoundingBoxLabels } from "./layers/BoundingBoxLabels"
import { ibmPlexSans } from "configuration/fonts"
import { getGlobalMapRef } from "./GlobalViewState"
import { IconMapNodes } from "./layers/icons/IconMapNodes"
import { getMapModals } from "plugins/plugin-registry"
import { MapNodes } from "./map-nodes/MapNodes"
import { BannerContainer } from "themes/default-tailwind/notifications/BannerContainer"
import { getComparisonHoverCursor } from "plugins/Orthomosaic/layers/useOrthoComparisonDivider"

const cursorStack = [`url("/images/cursors/cursor.svg"), auto`] as string[]
// A sticky cursor outranks deck.gl's hover cursor, so a tool that owns the
// click keeps its cursor over pickable layers.
const stickyCursors = new Set<string>()
export const getCursorStack = () => cursorStack
export const pushCursorStack = (cursor: string, { sticky = false } = {}) => {
    if (cursorStack.at(-1) === cursor) return
    cursorStack.push(cursor)
    if (sticky) stickyCursors.add(cursor)
}
export const popCursorStack = () => {
    if (cursorStack.length > 1) stickyCursors.delete(cursorStack.pop() as string)
}
export const resetCursorStack = () => {
    cursorStack.length = 1
    stickyCursors.clear()
}
const isStickyCursor = () => stickyCursors.has(cursorStack.at(-1) as string)

const deckglLighting = [
    new LightingEffect({
        cameraLight: new _CameraLight({
            color: [255, 255, 255],
            intensity: 1,
        }),
        ambientLight: new AmbientLight({
            color: [200, 200, 200],
            intensity: 2,
        }),
    }),
]

export const deckglEventEmitter = new EventEmitter()

type MapElementsProps = {
    layers: LayersList
    tooltipGenerator: (info: PickingInfo) => TooltipContent
}
export const MapElements = ({ layers, tooltipGenerator }: MapElementsProps) => {
    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())
    // These are for determining if map interaction should be disabled (if modals are open)
    const mapLayerDropdownOpen = useAppSelector(selectLayerDropdownOpen)
    const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition>()
    const someModalOpen = mapLayerDropdownOpen || !!contextMenuPosition // || (...list all conditions)

    const activeToolName = useAppSelector(selectActiveToolName())

    const isEditingFirePerimeter = !!useAppSelector(selectEditingFirePerimeterId())

    const isDrawingFirePerimeter = !!useAppSelector(selectEditingFirePerimeterId())

    const modals = useMemo(() => {
        const pluginModals = getMapModals()
        const modals = []
        for (const p of pluginModals) {
            modals.push(p.Modal)
        }
        return modals
    }, [])

    const hasLabels = useAppSelector(selectHasVisibleLabels)

    const showLiveChat = !pdfSnapshotOpen && !someModalOpen && !activeToolName
    const deckglOnClick = useCallback(
        (info: PickingInfo, event: MjolnirGestureEvent) => {
            if (event.rightButton) {
                if (info.pixel) {
                    if (activeToolName || isDrawingFirePerimeter || isEditingFirePerimeter) return

                    const [x, y] = [info.pixel[0], info.pixel[1]]
                    const map = getGlobalMapRef()?.current?.getMap()
                    const lngLat = map?.unproject?.([x, y])
                    const coordinate =
                        (lngLat && "lng" in lngLat && "lat" in lngLat
                            ? { longitude: lngLat.lng, latitude: lngLat.lat }
                            : undefined) ||
                        (info.coordinate && {
                            longitude: info.coordinate[0],
                            latitude: info.coordinate[1],
                        })

                    if (!coordinate) return

                    setContextMenuPosition({
                        top: y,
                        left: x,
                        coordinates: coordinate,
                        isMap: true,
                    })
                }
            } else {
                const deck = info.layer?.context.deck
                deckglEventEmitter.emit("click", info, event)
                if (deck && event.pointerType !== "mouse" && !isMapToolActive()) {
                    const picks = deck.pickMultipleObjects({
                        x: info.x,
                        y: info.y,
                        radius: 20,
                    })
                    const hotspot = picks?.find((pick) => pick.layer?.id === LayerId.Hotspots)
                    if (hotspot) {
                        return handleHotspotClick(hotspot, event as unknown as MouseEvent)
                    }
                }
            }
        },
        [activeToolName, isDrawingFirePerimeter, isEditingFirePerimeter]
    )

    return (
        <MapViewStateController>
            <div className={ibmPlexSans.className}>
                <div className="absolute top-0 w-full">
                    <BannerContainer />
                </div>
                <DeckGLOverlay
                    interleaved={true}
                    effects={deckglLighting}
                    onClick={deckglOnClick}
                    layers={layers}
                    getTooltip={tooltipGenerator}
                    getCursor={({ isHovering }) =>
                        isHovering && !isStickyCursor()
                            ? (getComparisonHoverCursor() ?? "pointer")
                            : (cursorStack.at(-1) as string)
                    }
                />
                <MapNodes />
                <IconMapNodes
                    contextMenuPosition={contextMenuPosition}
                    setContextMenuPosition={setContextMenuPosition}
                />
                {hasLabels && <MapboxLabel />}
                <BoundingBoxLabels />
                <SearchMarker />
                <MapboxCursors />

                {/* Live Chat Input */}
                {showLiveChat && <MapbboxCursorLiveChatInput />}

                {modals.map((Modal, i) => (
                    <Modal key={i} />
                ))}

                {contextMenuPosition && !activeToolName && !isDrawingFirePerimeter && (
                    <MapContextMenuContainer
                        position={contextMenuPosition}
                        setPosition={setContextMenuPosition}
                        mapLayer={contextMenuPosition.layer}
                    />
                )}
            </div>
        </MapViewStateController>
    )
}
